import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, errMsg, ROLES, REGIONS, TIER_LABEL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Field, ErrorBox, Spinner } from "../components/common";

const Shell = ({ title, intro, children }) => (
  <div className="kh-container grid min-h-[70vh] items-center gap-10 py-12 lg:grid-cols-2">
    <div className="kh-card p-6 sm:p-10">
      <h1 className="text-4xl">{title}</h1>
      {intro && <p className="mt-2 text-[#4a4f59]">{intro}</p>}
      <div className="mt-8">{children}</div>
    </div>
    <img src="/img/p4.jpg" alt="Celadon-glazed porcelain bowl" className="hidden h-[620px] w-full rounded-[2rem] object-cover lg:block" />
  </div>
);

export function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [f, setF] = useState({ email: sp.get("email") || "", password: "", remember: false });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setUnverified(false);
    try {
      const { data } = await api.post("/auth/login", f);
      signIn(data.token, data.user, f.remember);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
      nav(sp.get("next") || (data.user.role === "admin" ? "/admin" : "/dashboard"));
    } catch (er) {
      setErr(errMsg(er));
      setUnverified(er?.response?.status === 403 && /verify/i.test(errMsg(er)));
    } finally { setBusy(false); }
  };
  const resend = async () => {
    const { data } = await api.post("/auth/resend-verification", { email: f.email, origin_url: window.location.origin });
    toast.success(data.message);
    if (data.demo_verification_link) window.location.href = data.demo_verification_link;
  };
  return (
    <Shell title="Log in" intro="Welcome back to the Guild.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <div role="alert" className="rounded-xl bg-[#fdf2f1] p-3 text-sm text-[#7a1f16]" data-testid="login-error">{err}{unverified && <button type="button" onClick={resend} className="ml-2 underline" data-testid="resend-verification-btn">Resend verification</button>}</div>}
        <Field label="Email" id="l-email"><input id="l-email" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="kh-input" data-testid="login-email-input" /></Field>
        <Field label="Password" id="l-pw"><input id="l-pw" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="kh-input" data-testid="login-password-input" /></Field>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.remember} onChange={(e) => setF({ ...f, remember: e.target.checked })} className="h-4 w-4 accent-[#1F2A44]" data-testid="login-remember-checkbox" />Remember me</label>
          <Link to="/forgot-password" className="underline decoration-ochre" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
        <button disabled={busy} className="btn-primary w-full" data-testid="login-submit-btn">{busy ? "Logging in…" : "Log in"}</button>
        <p className="text-center text-sm">New here? <Link to="/register" className="font-semibold underline decoration-ochre" data-testid="login-register-link">Join the Guild</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", studio_name: "", member_type: "ceramic artist", region: "Pacific Northwest", tier: sp.get("tier") || "free", cycle: sp.get("cycle") || "monthly" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { const { data } = await api.post("/auth/register", { ...f, origin_url: window.location.origin }); setDone(data); } catch (er) { setErr(errMsg(er)); } finally { setBusy(false); }
  };
  if (done)
    return (
      <Shell title="Check your email" intro={`We sent a verification link to ${done.email}.`}>
        <div className="space-y-4" data-testid="register-success">
          <MailCheck className="h-10 w-10 text-celadon-dark" aria-hidden="true" />
          <p className="text-sm text-[#4a4f59]">Click the link in the email to verify your address{f.tier !== "free" ? " and continue to test checkout" : ""}.</p>
          <div className="rounded-xl bg-sand p-4 text-sm">
            <p className="font-semibold">Can't find the email?</p>
            <p className="mt-1 text-[#4a4f59]">You can verify your address straight away here:</p>
            <a href={done.demo_verification_link} className="btn-ochre btn-sm mt-3" data-testid="demo-verify-link">Verify my email</a>
          </div>
        </div>
      </Shell>
    );
  return (
    <Shell title="Join the Guild" intro="Create your account. You'll verify your email next.">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="register-form">
        {err && <div role="alert" className="rounded-xl bg-[#fdf2f1] p-3 text-sm text-[#7a1f16] sm:col-span-2" data-testid="register-error">{err}</div>}
        <Field label="Full name" id="r-name"><input id="r-name" required minLength={2} value={f.name} onChange={set("name")} className="kh-input" data-testid="register-name-input" /></Field>
        <Field label="Email" id="r-email"><input id="r-email" type="email" required value={f.email} onChange={set("email")} className="kh-input" data-testid="register-email-input" /></Field>
        <Field label="Password" id="r-pw" hint="At least 8 characters"><input id="r-pw" type="password" required minLength={8} value={f.password} onChange={set("password")} className="kh-input" data-testid="register-password-input" /></Field>
        <Field label="Studio or business name" id="r-studio"><input id="r-studio" value={f.studio_name} onChange={set("studio_name")} className="kh-input" data-testid="register-studio-input" /></Field>
        <Field label="I am a…" id="r-role"><select id="r-role" value={f.member_type} onChange={set("member_type")} className="kh-input" data-testid="register-role-select">{ROLES.map((r) => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Region" id="r-region"><select id="r-region" value={f.region} onChange={set("region")} className="kh-input" data-testid="register-region-select">{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Membership tier" id="r-tier"><select id="r-tier" value={f.tier} onChange={set("tier")} className="kh-input" data-testid="register-tier-select">{["free", "student", "professional", "studio"].map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}</select></Field>
        {f.tier !== "free" && <Field label="Billing" id="r-cycle"><select id="r-cycle" value={f.cycle} onChange={set("cycle")} className="kh-input" data-testid="register-cycle-select"><option value="monthly">Monthly</option><option value="annual">Annual</option></select></Field>}
        <button disabled={busy} className="btn-primary sm:col-span-2" data-testid="register-submit-btn">{busy ? "Creating account…" : "Create account"}</button>
        <p className="text-center text-sm sm:col-span-2">Already a member? <Link to="/login" className="font-semibold underline decoration-ochre">Log in</Link></p>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [err, setErr] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify", { token: sp.get("token") }).then(({ data }) => {
      signIn(data.token, data.user, true);
      toast.success("Email verified — welcome to the Guild!");
      const u = data.user;
      nav(u.status === "pending_payment" ? `/checkout?tier=${u.tier}&cycle=${u.billing_cycle || "monthly"}` : "/dashboard", { replace: true });
    }).catch((e) => setErr(errMsg(e)));
  }, [sp, nav, signIn]);
  return <div className="kh-container max-w-xl py-20">{err ? <ErrorBox message={err} /> : <Spinner label="Verifying your email…" />}</div>;
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin }); setMsg(data.message); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Reset your password" intro="We'll email you a link that expires in 1 hour.">
      {msg ? <div role="status" className="rounded-xl bg-celadon-light p-4 text-sm" data-testid="forgot-success">{msg}</div> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field label="Email" id="f-email"><input id="f-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="kh-input" data-testid="forgot-email-input" /></Field>
          <button disabled={busy} className="btn-primary w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("The two passwords don't match.");
    try { const { data } = await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success(data.message); nav("/login"); } catch (er) { setErr(errMsg(er)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {err && <div role="alert" className="rounded-xl bg-[#fdf2f1] p-3 text-sm text-[#7a1f16]" data-testid="reset-error">{err}</div>}
        <Field label="New password" id="rp-1" hint="At least 8 characters"><input id="rp-1" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="kh-input" data-testid="reset-password-input" /></Field>
        <Field label="Confirm new password" id="rp-2"><input id="rp-2" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="kh-input" data-testid="reset-password-confirm-input" /></Field>
        <button className="btn-primary w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </Shell>
  );
}
